<script>
import BookingEducator from '@/components/booking/bookingList/side-panel/booking-educator.vue'
import EmptyStateBlock from '@/components/general/empty-state-block.vue'
import Spinner from '@/components/general/spinner.vue'
import HeightTransition from '@/components/general/height-transition.vue'
import { mapActions } from 'vuex'

export default {
  name: 'BookingEducatorsTab',
  components: {
    Spinner,
    BookingEducator,
    HeightTransition,
    EmptyStateBlock,
  },
  props: {
    booking: {
      type: Object,
      default: null,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      educators: [],
      loading: true,
      error: null,
    }
  },
  watch: {
    booking: {
      handler() {
        this.resetState()
      },
      immediate: true,
    },
    isActive: {
      async handler(isActive) {
        if (!isActive || this.educators.length) return

        await this.loadEducators()
        this.loading = false
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      getMultiTentativeEducators: 'bookings/getMultiTentativeEducators',
    }),
    resetState() {
      this.educators = []
      this.loading = true
      this.error = null
    },
    async refresh() {
      if (this.loading) return

      this.resetState()
      await this.loadEducators()
      this.loading = false
    },
    async loadEducators() {
      if (!this.booking || !this.booking.id) return

      const { data, error } = await this.getMultiTentativeEducators(
        this.booking.id
      )

      if (error) {
        this.error = error
        return
      }

      this.educators = data?.candidates || []
    },
  },
}
</script>

<template>
  <HeightTransition content-transition="fade" mode="out-in">
    <Spinner v-if="loading" size="60" class="py-12" />
    <div v-else-if="educators.length > 0" class="educators-table">
      <v-row class="educators-table-header ma-0">
        <v-col cols="9" class="educators-table-heading d-flex align-center">
          {{ $t('sidePanel.educator') }}
        </v-col>
        <v-col
          cols="3"
          class="educators-table-heading pr-6 d-flex align-center"
        >
          {{ $t('sidePanel.status') }}
        </v-col>
      </v-row>
      <div class="educator-list">
        <div v-for="(educator, index) in educators" :key="educator.id">
          <BookingEducator :educator="educator" />
          <v-divider v-if="index < educators.length - 1" />
        </div>
      </div>
    </div>
    <EmptyStateBlock
      v-else-if="error"
      :title="$t('error.errorGenericTitle')"
      :description="$t('sidePanel.educatorsTab.errorMessage')"
    >
      <template v-slot:icon>
        <v-icon class="mb-4" color="red lighten-1" size="60"
          >mdi-alert-circle-outline</v-icon
        >
      </template>
    </EmptyStateBlock>
    <EmptyStateBlock
      v-else
      icon="mdi-account-search-outline"
      :title="$t('sidePanel.educatorsTab.noEducatorsFound')"
      :description="$t('sidePanel.educatorsTab.noEducatorsFoundDescription')"
    />
  </HeightTransition>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/_vue-transitions.scss';
@import '@/styles/bookingList/side-panel.scss';

.educator-list {
  max-height: 600px;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
